import { veevaVaultSearchLink } from '@atlas/core-utils/veeva-vault/links'
import { AxisCode, RequiredWorkOrderField } from '@atlas/types'
import type { WorkOrder } from '@/domain/work-order.types'
import { formatDeadline } from '@/lib/format-date'
import { jobFunctionOf } from '@/lib/option-order'
import type { DesignerOption } from './AssignDesignerCard'
import type { StageMarket } from './StageHeader'
import type { RequiredFieldRow } from './VerifyRequestCard'

/** One source-material code and where Open in Veeva takes it. */
export interface SourceMaterialCode {
	code: string
	url: string
}

/**
 * The order's original Veeva codes, the material the asset is made from, in their order.
 * Each opens its resolved Vault document, or a Vault search for the code when none was found.
 */
export const sourceMaterialCodes = (order: Pick<WorkOrder, 'originalCodes'>): SourceMaterialCode[] =>
	[...(order.originalCodes ?? [])]
		.sort((a, b) => a.position - b.position)
		.map(({ code, link }) => ({ code, url: link ?? veevaVaultSearchLink(code) }))

/** The related link the order carries for Veeva: where Open in Veeva goes for an order without codes. */
export const veevaRelatedLink = (order: Pick<WorkOrder, 'links'>): string | null =>
	order.links.find((link) => /veeva/i.test(link.name))?.url ?? null

/** A geography label without the ISO suffix the taxonomy appends: `Angola — AO` reads `Angola`. */
export const marketName = (label: string): string => label.replace(/\s+—\s+[A-Z]{2,3}$/, '')

/** The flag emoji for an ISO 3166-1 alpha-2 code, or null for anything else (a region has none). */
export function flagEmoji(isoCode: string | null | undefined): string | null {
	if (!isoCode || !/^[A-Za-z]{2}$/.test(isoCode)) return null
	const REGIONAL_INDICATOR_A = 0x1f1e6
	return String.fromCodePoint(...[...isoCode.toUpperCase()].map((c) => REGIONAL_INDICATOR_A + c.charCodeAt(0) - 65))
}

/** The people a designer can be picked from, by name, each with their job function when they have one. */
export const designerOptions = (
	users: readonly { userId: string; displayName: string; jobFunction?: string }[],
): DesignerOption[] =>
	users
		.map((user) => ({ userId: user.userId, name: user.displayName, role: jobFunctionOf(user) }))
		.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))

/**
 * Each required field as the Verify request card shows it. Brand and market fall back to their
 * codes until their names load, so a present value never reads as missing.
 */
export function requiredFieldRows(
	order: Pick<WorkOrder, 'assetTypeCode' | 'complexityLevel' | 'dueAt' | 'tuple' | 'campaignCode' | 'pack'>,
	fields: readonly RequiredWorkOrderField[],
	names: { brand: string | null; market: StageMarket | null },
): RequiredFieldRow[] {
	const market = names.market ? [names.market.flag, names.market.name].filter(Boolean).join(' ') : null
	const valueOf: Readonly<Record<RequiredWorkOrderField, string | null>> = {
		[RequiredWorkOrderField.ASSET_TYPE]: order.assetTypeCode,
		[RequiredWorkOrderField.COMPLEXITY]: order.complexityLevel,
		[RequiredWorkOrderField.BRAND]: names.brand ?? order.tuple?.[AxisCode.BRAND]?.[0] ?? null,
		[RequiredWorkOrderField.MARKET]: market ?? order.tuple?.[AxisCode.GEOGRAPHY]?.[0] ?? null,
		[RequiredWorkOrderField.DUE_DATE]: order.dueAt ? formatDeadline(order.dueAt) : null,
		[RequiredWorkOrderField.PLATFORM]: order.tuple?.[AxisCode.CHANNEL]?.[0] ?? null,
		[RequiredWorkOrderField.MAIN_COPY]: order.pack?.mainCopy?.content ?? null,
		// Whether the campaign has its files is the server's to say; with no campaign there is nothing to bring.
		[RequiredWorkOrderField.CAMPAIGN_MATERIALS]: order.campaignCode ?? 'No campaign',
	}
	return fields.map((field) => ({ field, value: valueOf[field] || null }))
}
